<template>
  <v-navigation-drawer
    :width="320 + sidePanelWidth"
    location="end"
    :temporary="temporary || !!sidePanelWidth"
    :permanent="!temporary && !sidePanelWidth"
    :scrim="temporary"
    disable-resize-watcher
    :disable-route-watcher="!!sidePanelWidth"
    :touchless="!!sidePanelWidth"
    :model-value="!!selectedTransferRequestId"
    @update:model-value="closeTransferRequestDrawer()"
  >
    <div class="h-full flex justify-end">
      <FilePreviewPanel
        v-if="mdAndUp && previewFileId"
        v-model:file-id="previewFileId"
        class="border-e border-gray-300 grow min-w-0"
        :files="filesWithAnalysis"
        :get-url="getFileUrl"
      />
      <CommentChat
        v-else-if="mdAndUp && commentsOpen"
        class="border-e border-gray-300 grow min-w-0"
        :account-id="props.accountId"
        :account="account"
        :targets="commentTargets"
        :comments="comments"
        :unread-count="unreadCount(commentTargets)"
        @close="commentsOpen = false"
      />
      <div class="w-[320px] shrink-0 h-full overflow-y-auto">
        <v-skeleton-loader
          v-if="selectedTransferRequestId && !selectedTransferRequest"
          type="article"
        />
        <div v-else-if="selectedTransferRequest">
          <v-toolbar density="compact" color="surface">
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeTransferRequestDrawer()"
            ></v-btn>
          </v-toolbar>
          <div class="px-4 pb-4 flex flex-col items-start">
            <div class="text-xs text-medium-emphasis">{{ t("transfers.transfer") }}</div>
            <div class="text-3xl mt-2" :class="{ 'mb-2': !balanceImpact }">
              {{ selectedTransferRequest.formattedAmount }}
            </div>
            <v-tooltip v-if="balanceImpact" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <div
                  v-bind="tooltipProps"
                  tabindex="0"
                  :aria-label="`${balanceImpact.formattedAfter}, ${t('transfers.balanceAfterThisRequest')}`"
                  class="mb-2 inline-flex w-fit self-start items-center gap-1 text-sm text-gray-500 dark:text-gray-300"
                >
                  {{ balanceImpact.formattedAfter }}
                  <v-icon icon="mdi-information-outline" size="x-small" />
                </div>
              </template>
              <span>{{ t("transfers.balanceAfterThisRequest") }}</span>
            </v-tooltip>
            <div class="flex flex-wrap items-center gap-2">
              <v-chip
                v-tooltip="selectedTransferRequest?.translatedStatus.tooltip"
                label
                append-icon="mdi-information-outline"
                :color="selectedTransferRequest?.color"
              >
                {{ selectedTransferRequest?.translatedStatus.title }}
              </v-chip>
              <v-tooltip v-if="similarTransferRequests.length" location="top" max-width="360">
                <template #activator="{ props: tooltipProps }">
                  <v-chip
                    v-bind="tooltipProps"
                    label
                    size="small"
                    color="error"
                    prepend-icon="mdi-alert"
                    append-icon="mdi-information-outline"
                  >
                    {{ t("transfers.similarRequest.chip") }}
                  </v-chip>
                </template>
                <span style="white-space: pre-line">{{ similarTransferRequestsTooltip }}</span>
              </v-tooltip>
            </div>
          </div>
          <v-divider class="mx-4" />

          <v-list v-if="details.length || filesWithAnalysis.length || hasCategories">
            <v-list-item
              v-for="detail in details"
              :key="detail.key"
              :to="detail.to"
              :append-icon="detail.to ? 'mdi-chevron-right' : undefined"
            >
              <v-list-item-title>{{ detail.title }}</v-list-item-title>
              <v-list-item-subtitle class="whitespace-pre-wrap" style="line-clamp: unset">
                {{ detail.value }}
              </v-list-item-subtitle>
              <v-list-item-subtitle
                v-if="detail.vop?.isNoMatch"
                class="opacity-100"
                style="line-clamp: unset"
              >
                <v-tooltip location="top">
                  <template #activator="{ props: tooltipProps }">
                    <v-chip
                      v-bind="tooltipProps"
                      :prepend-icon="detail.vop.icon"
                      :color="detail.vop.iconColor"
                      size="small"
                      class="mt-1"
                    >
                      {{ detail.vop.label }}
                    </v-chip>
                  </template>
                  <span style="white-space: pre-line">{{ detail.vop.tooltip }}</span>
                </v-tooltip>
              </v-list-item-subtitle>
              <template v-if="!detail.to && detail.vop && !detail.vop.isNoMatch" #append>
                <v-tooltip location="top">
                  <template #activator="{ props: tooltipProps }">
                    <v-icon
                      v-bind="tooltipProps"
                      size="small"
                      :color="detail.vop.iconColor"
                      :icon="detail.vop.icon"
                      style="opacity: 1"
                    />
                  </template>
                  <span style="white-space: pre-line">{{ detail.vop.tooltip }}</span>
                </v-tooltip>
              </template>
            </v-list-item>
            <div v-if="filesWithAnalysis.length" class="px-4 py-2 flex flex-col gap-2">
              <div class="text-sm">{{ t("common.files") }}</div>
              <TransferFileCard
                v-for="file in filesWithAnalysis"
                :key="file.id"
                :file="file"
                :active="file.id === previewFileId"
                :get-url="(disposition) => getFileUrl(file, disposition)"
                @preview="togglePreview(file.id)"
              >
                <div v-if="file.showFileAnalysisRow" class="flex flex-wrap gap-1 mt-2">
                  <v-tooltip v-if="file.extractedIban !== undefined" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-chip
                        v-bind="tooltipProps"
                        :prepend-icon="file.ibanMatch ? 'mdi-check-circle' : 'mdi-alert-circle'"
                        :color="file.ibanMatch ? 'success' : 'error'"
                        size="small"
                      >
                        {{
                          file.ibanMatch
                            ? t("transfers.files.ibanMatch")
                            : file.noIbanExtracted
                              ? t("transfers.files.ibanNotFoundLabel")
                              : t("transfers.files.ibanNoMatchLabel")
                        }}
                      </v-chip>
                    </template>
                    <span>{{
                      file.ibanMatch
                        ? t("transfers.files.ibanMatchTooltip")
                        : file.noIbanExtracted
                          ? t("transfers.files.ibanNotFoundTooltip")
                          : t("transfers.files.ibanNoMatchTooltip")
                    }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="file.hasAmountAnalysis" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-chip
                        v-bind="tooltipProps"
                        :prepend-icon="file.amountMatch ? 'mdi-check-circle' : 'mdi-alert-circle'"
                        :color="file.amountMatch ? 'success' : 'error'"
                        size="small"
                      >
                        {{
                          file.amountMatch
                            ? t("transfers.files.amountMatch")
                            : file.noAmountExtracted
                              ? t("transfers.files.amountNotFoundLabel")
                              : t("transfers.files.amountNoMatchLabel")
                        }}
                      </v-chip>
                    </template>
                    <span>{{
                      file.amountMatch
                        ? t("transfers.files.amountMatchTooltip")
                        : file.noAmountExtracted
                          ? t("transfers.files.amountNotFoundTooltip")
                          : t("transfers.files.amountNoMatchTooltip")
                    }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="file.extractedIssuerSiren === null" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-chip
                        v-bind="tooltipProps"
                        prepend-icon="mdi-domain-off"
                        color="warning"
                        size="small"
                      >
                        {{ t("transfers.files.noIssuerSiren") }}
                      </v-chip>
                    </template>
                    <span>{{ t("transfers.files.noIssuerSirenTooltip") }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="typeof file.invoiceIssuerInProcedureCollective === 'boolean'"
                    location="top"
                  >
                    <template #activator="{ props: tooltipProps }">
                      <v-chip
                        v-bind="tooltipProps"
                        :prepend-icon="
                          file.invoiceIssuerInProcedureCollective === true
                            ? 'mdi-alert-circle'
                            : 'mdi-check-circle'
                        "
                        :color="
                          file.invoiceIssuerInProcedureCollective === true ? 'error' : 'success'
                        "
                        size="small"
                      >
                        {{
                          file.invoiceIssuerInProcedureCollective === true
                            ? t("transfers.files.issuerProcedureCollective")
                            : t("transfers.files.issuerNoProcedureCollective")
                        }}
                      </v-chip>
                    </template>
                    <span>{{
                      file.invoiceIssuerInProcedureCollective === true
                        ? t("transfers.files.issuerProcedureCollectiveTooltip")
                        : t("transfers.files.issuerNoProcedureCollectiveTooltip")
                    }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="typeof file.invoiceIssuerHasConflictOfInterest === 'boolean'"
                    location="top"
                  >
                    <template #activator="{ props: tooltipProps }">
                      <v-chip
                        v-bind="tooltipProps"
                        :prepend-icon="
                          file.invoiceIssuerHasConflictOfInterest === true
                            ? 'mdi-alert-circle'
                            : 'mdi-check-circle'
                        "
                        :color="
                          file.invoiceIssuerHasConflictOfInterest === true ? 'error' : 'success'
                        "
                        size="small"
                      >
                        {{
                          file.invoiceIssuerHasConflictOfInterest === true
                            ? t("transfers.files.conflictOfInterest")
                            : t("transfers.files.noConflictOfInterest")
                        }}
                      </v-chip>
                    </template>
                    <span>{{ file.conflictOfInterestTooltip }}</span>
                  </v-tooltip>
                </div>
              </TransferFileCard>
            </div>
            <v-list-item
              v-if="
                hasCategories &&
                (!user.hasRole(supervisorRoles, { account }) ||
                  selectedTransferRequest?.status !== 'pending')
              "
            >
              <v-list-item-title>{{ t("transfers.category") }}</v-list-item-title>
              <v-list-item-subtitle>{{ selectedCategory?.name ?? "-" }} </v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <div
            v-if="hasCategories && selectedTransferRequest?.status === 'pending'"
            class="mx-3 mb-3"
          >
            <v-autocomplete
              v-if="user.hasRole(supervisorRoles, { account })"
              v-model="selectedCategoryId"
              :items="categories"
              item-title="name"
              item-value="id"
              :label="t('transfers.category')"
              :placeholder="t('transfers.selectCategory')"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              @update:model-value="updateCategory"
            ></v-autocomplete>

            <v-card v-if="selectedCategory" class="mt-3 p-3 border-theme" variant="flat">
              <div class="flex items-center justify-between text-xs text-medium-emphasis mb-2">
                <span>{{ t("transfers.budgetPreview.title") }}</span>
                <v-btn
                  style="position: absolute; right: 0; top: 0"
                  size="small"
                  icon="mdi-open-in-new"
                  variant="text"
                  :to="{ name: 'Budget', params: { accountId: props.accountId } }"
                ></v-btn>
              </div>
              <div class="flex justify-between text-sm mb-1">
                <span>{{ t("transfers.budgetPreview.projected") }}</span>
                <span :class="isOverBudget ? 'text-error font-medium' : ''">
                  {{ formattedProjectedAmount }}
                </span>
              </div>
              <v-progress-linear
                :model-value="Math.min(projectedPercentage, 100)"
                :color="progressColor"
                height="8"
                rounded
              ></v-progress-linear>
              <div class="flex justify-between text-xs text-medium-emphasis mt-1">
                <span>{{ formattedCurrentAmount }} {{ t("transfers.budgetPreview.used") }}</span>
                <span>{{ formattedBudget }} {{ t("transfers.budgetPreview.target") }}</span>
              </div>
              <v-chip
                v-if="isOverBudget"
                color="error"
                size="small"
                variant="tonal"
                class="mt-2"
                prepend-icon="mdi-alert"
              >
                {{ t("transfers.budgetPreview.overBudget") }}
              </v-chip>
              <v-chip
                v-else
                color="success"
                size="small"
                variant="tonal"
                class="mt-2"
                prepend-icon="mdi-check"
              >
                {{ t("transfers.budgetPreview.withinBudget") }}
              </v-chip>
            </v-card>
          </div>

          <CommentsListItem
            :comments="comments"
            :unread-count="unreadCount(commentTargets)"
            :active="commentsOpen"
            @click="toggleChat()"
          />

          <v-card v-if="allDecisionEntries.length > 0" class="m-3 p-2">
            <div class="text-xs mb-2">{{ t("transfers.decisions.title") }}</div>
            <v-list density="compact" class="p-0">
              <v-list-item v-for="entry in allDecisionEntries" :key="entry.key" class="px-0">
                <template #prepend>
                  <v-icon :color="entry.iconColor" size="small">
                    {{ entry.icon }}
                  </v-icon>
                </template>
                <v-list-item-title class="text-sm">{{ entry.label }}</v-list-item-title>
                <v-list-item-subtitle>{{ entry.subtitle }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card>

          <div v-if="selectedTransferRequest?.status === 'pending'" class="mx-3">
            <v-btn
              v-if="canMakeDecision"
              block
              color="primary"
              data-testid="transfer-request-approve"
              :disabled="user.isKind('admin') || isLoading"
              :loading="isLoading"
              @click="openConfirmDialog('approve')"
            >
              {{ t("transfers.actions.approve") }}
            </v-btn>

            <v-btn
              v-if="canMakeDecision"
              class="mt-2"
              block
              color="warn"
              data-testid="transfer-request-request-info"
              @click="
                previewFileId = null;
                commentsOpen = true;
              "
            >
              {{ t("transfers.actions.requestInfo") }}
            </v-btn>

            <v-btn
              v-if="canMakeDecision"
              class="mt-2"
              block
              color="error"
              data-testid="transfer-request-reject"
              :disabled="user.isKind('admin') || isLoading"
              :loading="isLoading"
              @click="openConfirmDialog('reject')"
            >
              {{ t("transfers.actions.reject") }}
            </v-btn>

            <AdminOnlyCard class="mt-3">
              {{ t("transfers.actions.adminCannotApprove") }}
            </AdminOnlyCard>
          </div>
          <div
            v-if="
              selectedTransferRequest?.status === 'pending' ||
              selectedTransferRequest?.status === 'accepted'
            "
            class="mx-3"
          >
            <v-btn
              v-if="canExecuteTreezorPayout"
              block
              color="primary"
              :disabled="isLoading"
              :loading="isLoading"
              @click="openConfirmDialog('execute')"
            >
              {{ t("transfers.actions.execute") }}
            </v-btn>
            <v-btn
              v-if="canManageTreezorTransfer"
              :class="canMakeDecision || canExecuteTreezorPayout ? 'mt-2' : undefined"
              block
              variant="outlined"
              color="error"
              :disabled="isLoading"
              :loading="isLoading"
              @click="openConfirmDialog('cancel')"
            >
              {{ t("transfers.actions.cancelTransfer") }}
            </v-btn>
          </div>
          <v-dialog v-model="confirmDialog" width="400" :persistent="isLoading">
            <v-card>
              <v-card-title>{{
                confirmAction === "approve"
                  ? t("transfers.actions.approveTransfer")
                  : confirmAction === "reject"
                    ? t("transfers.actions.rejectTransfer")
                    : confirmAction === "execute"
                      ? t("transfers.actions.executeTransferConfirm")
                      : t("transfers.actions.cancelTransferConfirm")
              }}</v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" :disabled="isLoading" @click="confirmDialog = false">{{
                  t("transfers.actions.cancel")
                }}</v-btn>
                <v-btn
                  variant="flat"
                  :color="
                    confirmAction === 'approve' || confirmAction === 'execute' ? 'primary' : 'error'
                  "
                  :loading="isLoading"
                  :disabled="isLoading"
                  @click="confirmTransferAction"
                >
                  {{
                    confirmAction === "approve"
                      ? t("transfers.actions.approve")
                      : confirmAction === "reject"
                        ? t("transfers.actions.reject")
                        : confirmAction === "execute"
                          ? t("transfers.actions.execute")
                          : t("transfers.actions.cancelTransfer")
                  }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </div>
    <v-dialog
      v-if="!mdAndUp"
      :model-value="!!previewFileId"
      fullscreen
      @update:model-value="previewFileId = null"
    >
      <v-card>
        <FilePreviewPanel
          v-model:file-id="previewFileId"
          :files="filesWithAnalysis"
          :get-url="getFileUrl"
        />
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="!mdAndUp"
      :model-value="commentsOpen"
      fullscreen
      @update:model-value="commentsOpen = false"
    >
      <v-card>
        <CommentChat
          v-if="commentsOpen"
          :account-id="props.accountId"
          :account="account"
          :targets="commentTargets"
          :comments="comments"
          :unread-count="unreadCount(commentTargets)"
          @close="commentsOpen = false"
        />
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import AdminOnlyCard from "@/components/AdminOnlyCard.vue";
import CommentChat from "@/components/CommentChat.vue";
import CommentsListItem from "@/components/CommentsListItem.vue";
import FilePreviewPanel from "@/components/FilePreviewPanel.vue";
import TransferFileCard from "@/components/TransferFileCard.vue";
import { useAccountCurrency } from "@/composables/useAccountCurrency";
import { useBalanceImpact } from "@/composables/useBalanceImpact";
import { useComments, useUnreadComments } from "@/composables/useComments";
import { useCollection, useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useTrackedCategories } from "@/composables/useTrackedCategories";
import { useTransferRequest } from "@/composables/useTransferRequest";
import { useTransferRequests } from "@/composables/useTransferRequests";
import { call, db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { useSelectedTransferRequestStore } from "@/store/selectedTransferRequest";
import { transferRequestCommentTargets } from "@/utils/comments";
import type { FileUrlDisposition } from "@/utils/files";
import { startAuthentication } from "@/utils/webAuthn";
import type { Account } from "@aircap/common/account";
import { supervisorRoles, transferRequestApproverRoles } from "@aircap/common/auth";
import type { SupervisorOrgUser } from "@aircap/common/supervisorOrg";
import { supervisorOrgUsersFullPath } from "@aircap/common/supervisorOrg";
import {
  hasSupervisorApproval,
  type TransactionFile,
  type VerificationOfPayee,
} from "@aircap/common/transaction";
import {
  treezorBeneficiariesFullPath,
  type Beneficiary as TreezorBeneficiary,
} from "@aircap/common/treezor";
import type { Timestamp } from "@firebase/firestore-types";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import { collection, deleteField, doc, getDocs, limit, query, where } from "firebase/firestore";
import { storeToRefs } from "pinia";
import { computed, ref, toRef, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { useDisplay } from "vuetify";

const props = withDefaults(
  defineProps<{
    accountId: string;
    temporary?: boolean;
  }>(),
  { temporary: false },
);

const { selectedTransferRequestId, commentsOpen } = storeToRefs(useSelectedTransferRequestStore());
const { closeTransferRequestDrawer } = useSelectedTransferRequestStore();

const { t, locale } = useI18n();
const {
  transferRequest: selectedTransferRequest,
  updateTransferRequest: updateSelectedTransferRequest,
} = useTransferRequest({
  accountId: toRef(() => props.accountId),
  transferRequestId: selectedTransferRequestId,
});
const { transferRequests } = useTransferRequests(toRef(() => props.accountId));
const { impactFor } = useBalanceImpact(toRef(() => props.accountId));
const balanceImpact = computed(() =>
  selectedTransferRequest.value ? impactFor(selectedTransferRequest.value) : undefined,
);

const similarTransferRequests = computed(() => {
  const current = selectedTransferRequest.value;
  if (!current || !transferRequests.value) return [];
  const currentIban = current.beneficiaryIban.replaceAll(/\s/g, "").toUpperCase();
  const currentCents = Math.round(parseFloat(current.amount) * 100);
  if (!Number.isFinite(currentCents)) return [];
  return transferRequests.value.filter((request) => {
    if (request.id === current.id || request.status !== "accepted") return false;
    if (request.beneficiaryIban.replaceAll(/\s/g, "").toUpperCase() !== currentIban) return false;
    return Math.round(parseFloat(request.amount) * 100) === currentCents;
  });
});

const similarTransferRequestsTooltip = computed(() => {
  const intro =
    similarTransferRequests.value.length === 1
      ? t("transfers.similarRequest.found")
      : t("transfers.similarRequest.foundMany", { count: similarTransferRequests.value.length });
  const details = similarTransferRequests.value
    .map((request) =>
      t("transfers.similarRequest.detail", {
        date: request.formattedDate,
        amount: request.formattedAmount,
        beneficiary: request.beneficiaryName,
      }),
    )
    .join("\n\n");
  return `${intro}\n\n${details}`;
});

const { showErrorToast, showSuccessToast } = useToast();
const user = useUserStore();

const { categories, hasCategories } = useTrackedCategories(
  toRef(() => props.accountId),
  "outgoing",
);
const { currencyCode } = useAccountCurrency(toRef(() => props.accountId));

const confirmDialog = ref(false);
const confirmAction = ref<"approve" | "reject" | "cancel" | "execute" | null>(null);
const reviewedEditedAt = ref<number | null>(null);
const reviewedRequestId = ref<string | null>(null);
const isLoading = ref(false);
const previewFileId = ref<string | null>(null);

const currentEditedAt = () => {
  const editedAtMillis = selectedTransferRequest.value?.editedAt?.toMillis();
  return editedAtMillis === undefined ? null : Math.floor(editedAtMillis);
};

watch(selectedTransferRequestId, () => {
  previewFileId.value = null;
  reviewedRequestId.value = null;
});

watch(
  () => (selectedTransferRequestId.value ? selectedTransferRequest.value?.id : undefined),
  (id) => {
    if (!id || id === reviewedRequestId.value) return;
    reviewedRequestId.value = id;
    reviewedEditedAt.value = currentEditedAt();
  },
);

const togglePreview = (fileId: string) => {
  previewFileId.value = previewFileId.value === fileId ? null : fileId;
  commentsOpen.value = false;
};
const toggleChat = () => {
  commentsOpen.value = !commentsOpen.value;
  previewFileId.value = null;
};

const commentTargets = computed(() =>
  selectedTransferRequest.value ? transferRequestCommentTargets(selectedTransferRequest.value) : [],
);
const { comments } = useComments(() => props.accountId, commentTargets);
const { unreadCount } = useUnreadComments(() => props.accountId);

const { mdAndUp, width: displayWidth } = useDisplay();
const sidePanelWidth = computed(() => {
  if (!mdAndUp.value) return 0;
  if (previewFileId.value) return Math.min(900, Math.round(displayWidth.value * 0.6));
  if (commentsOpen.value) return 400;
  return 0;
});

const selectedCategoryId = ref<string | null>(null);

watch(
  () => selectedTransferRequest.value?.trackedCategoryId,
  (categoryId) => {
    if (!categoryId) {
      selectedCategoryId.value = null;
      return;
    }
    const categoryExists = categories.value.some((c) => c.id === categoryId);
    selectedCategoryId.value = categoryExists ? categoryId : null;
  },
  { immediate: true },
);

const selectedCategory = computed(() => {
  if (!selectedCategoryId.value) return null;
  return categories.value.find((c) => c.id === selectedCategoryId.value) ?? null;
});

const transferAmount = computed(() =>
  selectedTransferRequest.value?.amount ? parseFloat(selectedTransferRequest.value.amount) : 0,
);

const usedAmount = computed(() => {
  if (!selectedCategory.value) return 0;
  if (
    selectedCategory.value.completedAt &&
    selectedCategory.value.currentAmount < selectedCategory.value.amount
  ) {
    return selectedCategory.value.amount;
  }
  return selectedCategory.value.currentAmount;
});

const projectedAmount = computed(() => {
  if (!selectedCategory.value) return 0;
  const cash = selectedCategory.value.currentAmount + transferAmount.value;
  if (selectedCategory.value.completedAt && cash < selectedCategory.value.amount) {
    return selectedCategory.value.amount;
  }
  return cash;
});

const projectedPercentage = computed(() => {
  if (!selectedCategory.value || selectedCategory.value.amount === 0) return 0;
  return (projectedAmount.value / selectedCategory.value.amount) * 100;
});

const isOverBudget = computed(() => {
  if (!selectedCategory.value) return false;
  return (
    selectedCategory.value.currentAmount + transferAmount.value > selectedCategory.value.amount
  );
});

const progressColor = computed(() => {
  if (isOverBudget.value) return "error";
  if (projectedPercentage.value >= 100) return "#4CAF50";
  return "primary";
});

const formatAmount = (amount: number) => {
  return new Intl.NumberFormat(locale.value, {
    style: "currency",
    currency: currencyCode.value ?? "EUR",
  }).format(amount);
};

const formattedCurrentAmount = computed(() =>
  selectedCategory.value ? formatAmount(usedAmount.value) : "",
);

const formattedBudget = computed(() =>
  selectedCategory.value ? formatAmount(selectedCategory.value.amount) : "",
);

const formattedProjectedAmount = computed(() => formatAmount(projectedAmount.value));

const updateCategory = async (categoryId: string | null) => {
  if (!selectedTransferRequest.value?.id) return;

  try {
    console.log("Updating transfer category...");
    await updateSelectedTransferRequest({
      trackedCategoryId: categoryId ?? deleteField(),
    });
  } catch (cause) {
    const error = new Error("Error updating category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const accountRef = computed(() => doc<Account>(db, "accounts", props.accountId));
const { data: account } = useDocument<Account>(() => accountRef.value);

const canManageTreezorTransfer = computed(
  () => user.isKind("user") && account.value?.accountType === "treezor",
);
const canExecuteTreezorPayout = computed(
  () => canManageTreezorTransfer.value && selectedTransferRequest.value?.status === "accepted",
);

const readableSupervisorOrgId = computed(() => {
  if (!account.value?.supervisorOrgId) return null;
  if (!user.isKind("admin") && user.supervisorOrgId !== account.value.supervisorOrgId) return null;
  return account.value.supervisorOrgId;
});
const usersRef = computed(() =>
  readableSupervisorOrgId.value
    ? collection<SupervisorOrgUser>(db, supervisorOrgUsersFullPath(readableSupervisorOrgId.value))
    : null,
);
const { data: supervisorOrgUsers, pending: supervisorOrgUsersPending } =
  useCollection<SupervisorOrgUser>(() => usersRef.value ?? undefined);

const requiredExternalApproverApprovals = computed(() => account.value?.externalApprovers ?? []);
const currentDecisions = computed(() => selectedTransferRequest.value?.decisions ?? {});
const approvalUids = computed(() =>
  Object.entries(currentDecisions.value)
    .filter(([, d]) => d.status === "approved")
    .map(([uid]) => uid),
);
const userHasDecided = computed(() =>
  user.uid ? currentDecisions.value[user.uid] !== undefined : false,
);
const requiresMultiSignature = computed(() => requiredExternalApproverApprovals.value.length > 0);

const supervisorHasApproved = computed(() =>
  hasSupervisorApproval(approvalUids.value, supervisorOrgUsers.value),
);
const supervisorApprovalLoading = computed(
  () => !!usersRef.value && supervisorOrgUsersPending.value,
);

const canMakeDecision = computed(() => {
  if (!user.hasRole(transferRequestApproverRoles, { account: account.value })) return false;
  if (userHasDecided.value) return false;
  if (supervisorApprovalLoading.value) return false;

  if (
    user.hasRole(["supervisor.supervisor"], { account: account.value }) &&
    supervisorHasApproved.value
  )
    return false;

  return true;
});

const userEmailByUid = computed(() => {
  const map = new Map<string, string>();
  supervisorOrgUsers.value?.forEach((user) => {
    if (user.uid) {
      map.set(user.uid, user.email);
    }
  });
  return map;
});

const allDecisionEntries = computed(() => {
  const entries: {
    key: string;
    label: string;
    subtitle: string;
    icon: string;
    iconColor: string;
    sortOrder: number;
  }[] = [];

  const decisions = currentDecisions.value;
  const isPending = selectedTransferRequest.value?.status === "pending";

  if (decisions && Object.keys(decisions).length > 0) {
    for (const [uid, decision] of Object.entries(decisions)) {
      const isApproved = decision.status === "approved";
      const date = decision.createdAt
        ? formatDateTime(decision.createdAt)
        : t("transfers.statusUpdated.unknownDate");

      entries.push({
        key: `decision-${uid}`,
        label: userEmailByUid.value.get(uid) ?? t("transfers.statusUpdated.unknownUser"),
        subtitle: `${t(`transfers.decisions.${decision.status}`)} · ${date}`,
        icon: isApproved ? "mdi-check-circle" : "mdi-close-circle",
        iconColor: isApproved ? "success" : "error",
        sortOrder: decision.createdAt?.toMillis?.() ?? 0,
      });
    }
  }

  if ((requiresMultiSignature.value || entries.length > 0) && isPending) {
    for (const uid of requiredExternalApproverApprovals.value) {
      if (!decisions?.[uid]) {
        entries.push({
          key: `pending-externalApprover-${uid}`,
          label: userEmailByUid.value.get(uid) ?? t("transfers.statusUpdated.unknownUser"),
          subtitle: t("transfers.decisions.pending"),
          icon: "mdi-circle-outline",
          iconColor: "#9E9E9E",
          sortOrder: -1,
        });
      }
    }

    if (usersRef.value && !supervisorApprovalLoading.value && !supervisorHasApproved.value) {
      entries.push({
        key: "pending-supervisor",
        label: t("transfers.approvals.supervisorApproval"),
        subtitle: t("transfers.decisions.pending"),
        icon: "mdi-circle-outline",
        iconColor: "#9E9E9E",
        sortOrder: -2,
      });
    }
  }

  return entries.sort((a, b) => b.sortOrder - a.sortOrder);
});

const formatDateTime = (date: Timestamp) => {
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date.toDate());
};

const AMOUNT_MATCH_TOLERANCE = 0.01;

function normalizeIban(iban: string) {
  return iban.replace(/\s/g, "").toUpperCase();
}

type TransferFileAnalysisRow = TransactionFile & {
  ibanMatch: boolean;
  noIbanExtracted: boolean;
  amountMatch: boolean;
  noAmountExtracted: boolean;
  hasAmountAnalysis: boolean;
  hasInvoiceIssuerAnalysis: boolean;
  showFileAnalysisRow: boolean;
  conflictOfInterestTooltip: string;
};

const filesWithAnalysis = computed((): TransferFileAnalysisRow[] => {
  const transfer = selectedTransferRequest.value;
  const currency = currencyCode.value;
  const list = transfer?.files ?? [];

  return list.map((file): TransferFileAnalysisRow => {
    const extractedIbanStr = file.extractedIban;
    const ibanMatch =
      transfer && extractedIbanStr
        ? normalizeIban(extractedIbanStr) === normalizeIban(transfer.beneficiaryIban)
        : false;

    const noIbanExtracted = file.extractedIban === null;

    let amountMatch = false;
    if (typeof file.extractedAmount === "number" && transfer && currency) {
      const transferAmount = Math.abs(parseFloat(transfer.amount));
      const transferCurrency = currency.toUpperCase();
      if (file.extractedCurrency && file.extractedCurrency.toUpperCase() === transferCurrency) {
        amountMatch = Math.abs(file.extractedAmount - transferAmount) < AMOUNT_MATCH_TOLERANCE;
      }
    }
    const noAmountExtracted = file.extractedAmount === null;

    const hasAmountAnalysis = file.extractedAmount !== undefined;

    const hasInvoiceIssuerAnalysis =
      file.extractedIssuerSiren !== undefined ||
      file.invoiceIssuerInProcedureCollective !== undefined ||
      file.invoiceIssuerHasConflictOfInterest !== undefined;

    const showFileAnalysisRow =
      file.extractedIban !== undefined || hasAmountAnalysis || hasInvoiceIssuerAnalysis;

    let conflictOfInterestTooltipStr: string;
    if (file.invoiceIssuerHasConflictOfInterest !== true) {
      conflictOfInterestTooltipStr = t("transfers.files.noConflictOfInterestTooltip");
    } else if (user.isKind("admin") && file.invoiceIssuerConflictOfInterestReason) {
      conflictOfInterestTooltipStr = `[admin only] ${file.invoiceIssuerConflictOfInterestReason}`;
    } else {
      conflictOfInterestTooltipStr = t("transfers.files.conflictOfInterestTooltip");
    }

    return {
      ...file,
      ibanMatch,
      noIbanExtracted,
      amountMatch,
      noAmountExtracted,
      hasAmountAnalysis,
      hasInvoiceIssuerAnalysis,
      showFileAnalysisRow,
      conflictOfInterestTooltip: conflictOfInterestTooltipStr,
    };
  });
});

type VopDisplay = {
  label: string;
  iconColor: string;
  icon: string;
  isNoMatch: boolean;
  tooltip: string;
};

const vopColorMap: Record<VerificationOfPayee, string> = {
  Match: "success",
  CloseMatch: "success",
  NoMatch: "error",
  VerificationNotPossible: "warning",
};

function getVopDisplay(status: VerificationOfPayee | undefined) {
  if (!status) return undefined;
  return {
    label: t(`transactions.details.verificationOfPayee.${status}`),
    iconColor: vopColorMap[status],
    icon:
      status === "NoMatch"
        ? "mdi-alert-circle"
        : status === "VerificationNotPossible"
          ? "mdi-shield-alert"
          : "mdi-shield-check",
    isNoMatch: status === "NoMatch",
    tooltip: t(`transactions.details.verificationOfPayee.tooltip${status}`),
  };
}

type Detail = {
  key: string;
  title: string;
  value: string;
  to?: RouteLocationRaw;
  vop?: VopDisplay;
};

const details = computed<Detail[]>(() => {
  if (!selectedTransferRequest.value) return [];

  return [
    {
      key: "beneficiaryName",
      title: t("transfers.beneficiaryName"),
      value: selectedTransferRequest.value.beneficiaryName,
      vop: getVopDisplay(selectedTransferRequest.value.verificationOfPayee),
    },
    {
      key: "beneficiaryIban",
      title: t("transfers.beneficiaryIban"),
      value: selectedTransferRequest.value.beneficiaryIban,
      to: {
        name: "BeneficiaryDetail",
        params: {
          accountId: props.accountId,
          beneficiaryIban: selectedTransferRequest.value.beneficiaryIban,
        },
      },
    },
    {
      key: "amount",
      title: t("common.amount"),
      value: selectedTransferRequest.value.formattedAmount,
    },
    {
      key: "date",
      title: t("common.date"),
      value: selectedTransferRequest.value.formattedDate,
    },
    ...(selectedTransferRequest.value.label
      ? [
          {
            key: "label",
            title: t("transactions.details.label"),
            value: selectedTransferRequest.value.label,
          },
        ]
      : []),
    ...(selectedTransferRequest.value.reference
      ? [
          {
            key: "reference",
            title: t("transactions.details.reference"),
            value: selectedTransferRequest.value.reference,
          },
        ]
      : []),
  ];
});

const getFileUrl = async (file: TransactionFile, disposition: FileUrlDisposition) => {
  if (!selectedTransferRequest.value?.id) throw new Error("No transfer request selected");
  const { url } = await call("getTransferRequestFileUrl")({
    accountId: props.accountId,
    transferId: selectedTransferRequest.value.id,
    fileId: file.id,
    disposition,
  });
  return url;
};

const openConfirmDialog = (action: "approve" | "reject" | "cancel" | "execute") => {
  if (
    (action === "approve" || action === "reject") &&
    currentEditedAt() !== reviewedEditedAt.value
  ) {
    reviewedEditedAt.value = currentEditedAt();
    showErrorToast(t("transfers.error.edited"));
    return;
  }
  confirmAction.value = action;
  confirmDialog.value = true;
};

const confirmTransferAction = async () => {
  if (!selectedTransferRequest.value || !confirmAction.value) return;

  const action = confirmAction.value;
  isLoading.value = true;
  try {
    console.log("Confirming transfer action...");
    switch (action) {
      case "approve":
      case "reject":
        await call("reviewTransferRequest")({
          accountId: props.accountId,
          transferId: selectedTransferRequest.value.id,
          action,
          editedAt: reviewedEditedAt.value,
        });
        break;
      case "cancel":
        await call("cancelTransferRequest")({
          accountId: props.accountId,
          transferId: selectedTransferRequest.value.id,
        });
        break;
      case "execute": {
        const transfer = selectedTransferRequest.value;
        const amount = parseFloat(transfer.amount);
        const validation = await call("validateTreezorBeneficiary")({
          name: transfer.beneficiaryName,
          iban: transfer.beneficiaryIban,
        });
        const beneficiariesSnap = await getDocs(
          query(
            collection<TreezorBeneficiary>(db, treezorBeneficiariesFullPath(props.accountId)),
            where<TreezorBeneficiary>(
              "metadata.iban",
              "==",
              normalizeIban(transfer.beneficiaryIban),
            ),
            limit(1),
          ),
        );
        const beneficiary = beneficiariesSnap.docs[0];
        if (!beneficiary) throw new Error("Beneficiary not found");

        const beneficiaryId = parseInt(beneficiary.id, 10);
        const { webAuthnAuthenticationOptions } = await call("createTreezorPayoutSCAPayload")({
          accountId: props.accountId,
          beneficiaryId,
          amount,
        });
        const webAuthnAuthenticationResponse = await startAuthentication({
          optionsJSON: webAuthnAuthenticationOptions,
        });
        await call("createTreezorPayout")({
          accountId: props.accountId,
          beneficiaryId,
          amount,
          beneficiaryValidationId: validation.beneficiaryValidationId,
          ...(transfer.label ? { label: transfer.label } : {}),
          ...(transfer.reference ? { reference: transfer.reference } : {}),
          webAuthnAuthenticationResponse,
          transferRequestId: transfer.id,
        });
        break;
      }
    }
    switch (action) {
      case "approve":
        showSuccessToast(t("transfers.success.validated"));
        break;
      case "reject":
        showSuccessToast(t("transfers.success.rejected"));
        break;
      case "cancel":
        showSuccessToast(t("transfers.success.cancelled"));
        break;
      case "execute":
        showSuccessToast(t("transfers.success.executed"));
        break;
    }
    confirmDialog.value = false;
  } catch (cause) {
    const error = new Error(
      `Error ${action === "approve" ? "approving" : action === "reject" ? "rejecting" : action === "cancel" ? "cancelling" : "executing"} transfer`,
      { cause },
    );
    console.error(error);
    if (cause instanceof FirebaseError && cause.code === "functions/aborted") {
      reviewedEditedAt.value = currentEditedAt();
      showErrorToast(t("transfers.error.edited"));
      confirmDialog.value = false;
      return;
    }
    captureException(error);
    switch (action) {
      case "approve":
        showErrorToast(t("transfers.error.validating"));
        break;
      case "reject":
        showErrorToast(t("transfers.error.rejecting"));
        break;
      case "cancel":
        showErrorToast(t("transfers.error.cancelling"));
        break;
      case "execute":
        showErrorToast(cause instanceof Error ? cause.message : t("transfers.error.executing"));
        break;
    }
    confirmDialog.value = false;
  } finally {
    isLoading.value = false;
  }
};
</script>
